import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Container, KeenIcon } from '@/components';
import {
  Toolbar,
  ToolbarHeading,
  ToolbarPageTitle,
  ToolbarDescription,
  ToolbarActions,
} from '@/partials/toolbar';
import {
  useGetEmailTemplateByIdQuery,
  useUpdateEmailTemplateMutation,
} from '@/services/EmailTemplateService';
import { Editor } from '@tinymce/tinymce-react';
import { TINYMCE_SCRIPT_SRC } from '@/constant';
import { EmailTemplateResponse } from '@/types/email-template-management.types';

const LANGS = ['en', 'fi', 'de', 'sv'] as const;

type Lang = (typeof LANGS)[number];

const EmailTemplateDetails = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const qs = new URLSearchParams(location.search);
  const initialMode = (qs.get('mode') as 'view' | 'edit') || 'view';
  const [mode, setMode] = useState<'view' | 'edit'>(initialMode);
  // Displaying all languages on one page; no active tab needed

  // Prefer template from navigation state to avoid needing a single-item API
  const navState = location.state as {
    template?: EmailTemplateResponse;
  } | null;
  const [template, setTemplate] = useState<EmailTemplateResponse | null>(
    navState?.template || null
  );

  // If state not passed, fall back to loading EN to show meta (name) quickly
  const { data: enData } = useGetEmailTemplateByIdQuery(
    { id: id || '', lang: 'en' },
    { skip: !id }
  );
  useEffect(() => {
    if (!template && enData?.data) {
      setTemplate(enData.data);
    }
  }, [enData, template]);

  // Fetch other languages to hydrate per-language fields
  const { data: fiData } = useGetEmailTemplateByIdQuery(
    { id: id || '', lang: 'fi' },
    { skip: !id }
  );

  const { data: deData } = useGetEmailTemplateByIdQuery(
    { id: id || '', lang: 'de' },
    { skip: !id }
  );
  const { data: svData } = useGetEmailTemplateByIdQuery(
    { id: id || '', lang: 'sv' },
    { skip: !id }
  );

  // Subjects state removed: body-only editing per requirements
  const [bodies, setBodies] = useState<Record<Lang, string>>({
    en: '',
    fi: '',
    de: '',
    sv: '',
  });

  useEffect(() => {
    // Build fresh objects from API data only to avoid depending on local state
    const nextBodies: Record<Lang, string> = { en: '', fi: '', de: '', sv: '' };

    if (enData?.data) {
      nextBodies.en = enData.data.body || enData.data.body_en || '';
    }
    if (fiData?.data) {
      nextBodies.fi = fiData.data.body_fi || '';
    }
    if (deData?.data) {
      nextBodies.de = deData.data.body_de || '';
    }
    if (svData?.data) {
      nextBodies.sv = svData.data.body_sv || '';
    }

    setBodies(nextBodies);
  }, [enData, fiData, deData, svData]);

  const [updateTemplate, { isLoading: isSaving } = { isLoading: false }] =
    useUpdateEmailTemplateMutation();

  const handleSave = async () => {
    if (!id) return;
    try {
      const payload = {
        body: bodies.en,
        body_fi: bodies.fi,
        body_de: bodies.de,
        body_sv: bodies.sv,
      };
      await updateTemplate({ id, data: payload, lang: 'en' }).unwrap();
      setMode('view');
      // Update local template state with new values for all languages
      setTemplate((prev) =>
        prev
          ? {
              ...prev,
              body: payload.body,
              body_fi: payload.body_fi,
              body_de: payload.body_de,
              body_sv: payload.body_sv,
              updated_at: new Date().toISOString(),
            }
          : prev
      );
    } catch (e) {
      // errors handled by baseQuery toast
      console.error(e);
    }
  };

  const headerTitle = useMemo(() => {
    return mode === 'edit'
      ? 'Edit Email Template Body'
      : 'Email Template Body Details';
  }, [mode]);

  return (
    <>
      <Container>
        <Toolbar>
          <ToolbarHeading>
            <ToolbarPageTitle text={headerTitle} />
            <ToolbarDescription>{headerTitle}</ToolbarDescription>
          </ToolbarHeading>
          <ToolbarActions>
            {mode === 'view' ? (
              <button
                type="button"
                className="btn btn-sm"
                onClick={() => setMode('edit')}
              >
                <KeenIcon type="filled" icon="pencil" /> Edit
              </button>
            ) : (
              <div className="flex gap-2">
                <button
                  type="button"
                  className="btn btn-sm btn-light"
                  onClick={() => setMode('view')}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  onClick={handleSave}
                  disabled={isSaving}
                >
                  {isSaving ? 'Saving...' : 'Save'}
                </button>
              </div>
            )}
            <button
              type="button"
              className="btn btn-sm btn-light"
              onClick={() => navigate(-1)}
            >
              Back
            </button>
          </ToolbarActions>
        </Toolbar>
      </Container>

      <Container>
        <div className="card">
          <div className="card-body space-y-10">
            {!template && (
              <div className="text-gray-600">Loading template content...</div>
            )}
            {!!template && (
              <>
                {LANGS.map((lng) => (
                  <div key={lng} className="space-y-4">
                    <h1 className="text-base font-semibold text-gray-800">
                      {lng.toUpperCase()} Body
                    </h1>
                    <div>
                      {mode === 'view' ? (
                        <>
                          <div className="block text-sm font-medium mb-1">
                            Body HTML ({lng.toUpperCase()})
                          </div>
                          <div
                            className="prose max-w-none border rounded-md p-3 bg-white"
                            dangerouslySetInnerHTML={{
                              __html: bodies[lng] || '',
                            }}
                          />
                        </>
                      ) : (
                        <>
                          <label
                            id={`body-html-label-${lng}`}
                            htmlFor={`template-editor-${lng}`}
                            className="block text-sm font-medium mb-1"
                          >
                            Body HTML ({lng.toUpperCase()})
                          </label>
                          <Editor
                            tinymceScriptSrc={TINYMCE_SCRIPT_SRC}
                            id={`template-editor-${lng}`}
                            value={bodies[lng]}
                            onEditorChange={(content: string) =>
                              setBodies((b) => ({ ...b, [lng]: content }))
                            }
                            aria-label={`Email template body editor for ${lng.toUpperCase()} language`}
                            aria-labelledby={`body-html-label-${lng}`}
                            init={{
                              height: 450,
                              menubar: false,
                              statusbar: true,
                              plugins: [
                                'lists',
                                'link',
                                'autolink',
                                'table',
                                'paste',
                                'wordcount',
                                'preview',
                                'image',
                                'code',
                              ],
                              toolbar:
                                'undo redo | bold italic underline forecolor backcolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | removeformat | table link preview',
                              content_style:
                                'body { font-family: Arial, sans-serif; font-size:14px }' +
                                ' .mce-content-body .mce-item-table pre .Y2IQFc { white-space: break-spaces !important; }' +
                                ' .mce-content-body pre { white-space: break-spaces; }',
                              paste_data_images: true,
                              branding: false,
                              iframe_aria_text: `Email template editor - ${lng.toUpperCase()}`,
                              aria_label: `Email template body editor for ${lng.toUpperCase()} language`,
                              setup: (editor: {
                                on: (
                                  event: string,
                                  callback: () => void
                                ) => void;
                                getContainer: () => HTMLElement | null;
                              }) => {
                                editor.on('init', () => {
                                  // Fix accessibility issues with TinyMCE elements
                                  // Wait for statusbar to be rendered
                                  setTimeout(() => {
                                    const container = editor.getContainer();
                                    if (container) {
                                      // Fix resize handle: add role to div with aria-label
                                      const resizeHandle =
                                        container.querySelector(
                                          '[data-mce-name="resize-handle"]'
                                        ) as HTMLElement;
                                      if (resizeHandle) {
                                        resizeHandle.setAttribute(
                                          'role',
                                          'button'
                                        );
                                      }

                                      // Fix statusbar navigation: add unique aria-label
                                      const statusbarNav =
                                        container.querySelector(
                                          '.tox-statusbar__path[role="navigation"]'
                                        ) as HTMLElement;
                                      if (statusbarNav) {
                                        statusbarNav.setAttribute(
                                          'aria-label',
                                          `Editor path navigation for ${lng.toUpperCase()} language`
                                        );
                                      }
                                    }
                                  }, 100);
                                });
                              },
                            }}
                          />
                        </>
                      )}
                    </div>
                    <hr className="border-gray-200" />
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      </Container>
    </>
  );
};

export default EmailTemplateDetails;
